<template>
  <div>
    <slot v-if="loading" name="loading-content">
      <ProductCardSkeleton
        v-for="index in Math.min(productCountLoading, perPage)"
        :key="`product-loading-${index}`"
        type="custom"
        class="col-span-12 sm:col-span-6 xl:col-span-4 2xl:col-span-3"
      />
    </slot>

    <template v-else>
      <Disruptor
        v-for="disruptor in disruptors"
        :key="disruptor._uid"
        :blok="disruptor"
        :style="{
          order: `calc(var(--plp-column-count) * ${disruptor.insert_in_row} + 1)`,
        }"
        data-testid="list_teaser"
      />
      <slot
        v-for="(product, index) in products || []"
        name="product"
        :product="product"
        :loading="loading"
        :refreshing="refreshing"
      >
        <Card
          :key="`product-${product.id}`"
          color-chip-size="sm"
          color-chip-rounded-size="sm"
          sibling-spacing="narrow"
          :index="index"
          :product="product"
          :placement="placement"
          :style="{ order: index + 1 }"
          class="col-span-12 border-t border-gray-400 sm:col-span-6 xl:col-span-4 2xl:col-span-3"
          @click:select-item="$emit('click:product', product, index)"
          @intersect:product="collectRowIntersection(index)"
        >
          <template #header-badge>
            <ProductListHeaderBadges :product="product" />
          </template>
        </Card>
      </slot>
    </template>
  </div>
</template>

<script setup lang="ts">
import { getDisruptorsForRow } from '@scayle/storefront-nuxt'
import {
  type Product,
  getRowByIndex,
  isFirstIndexOfRow,
} from '@scayle/storefront-nuxt'
import { defineAsyncComponent, hydrateOnVisible } from 'vue'
import type { SbListingDisruptor } from '~/storyblok/types/storyblok.gen'

type Props = {
  products?: Product[]
  loading?: boolean
  refreshing?: boolean
  disruptors?: SbListingDisruptor[]
  perPage?: number
  productCountLoading?: number
}

const props = withDefaults(defineProps<Props>(), {
  products: () => [],
  loading: true,
  refreshing: true,
  disruptors: () => [],
  perPage: PRODUCTS_PER_PAGE,
  productCountLoading: PRODUCTS_PER_PAGE,
})

const Disruptor = defineAsyncComponent({
  loader: () => import('~/components/cms/ListingDisruptor.vue'),
  hydrate: hydrateOnVisible(),
})

const Card = defineAsyncComponent({
  loader: () => import('~/components/product/card/ProductCard.vue'),
  hydrate: hydrateOnVisible(),
})

const { isGreaterOrEquals } = useViewport()

const emit = defineEmits<{
  (
    e: 'intersect:row',
    value: { row: number; items: Product[]; itemsPerRow: number },
  ): void
  (e: 'click:product', value: Product, i: number): void
}>()
const route = useRoute()

const currentPage = computed(() => parseInt(route.query.page as string) || 1)

/**
 * columns:
 * < 640px 1
 * >= 640px 2
 * >= 1280px 3
 * >= 1440px 4
 */
const columns = computed(() => {
  if (isGreaterOrEquals('2xl')) {
    return 4
  }
  if (isGreaterOrEquals('xl')) {
    return 3
  }
  return isGreaterOrEquals('sm') ? 2 : 1
})

/**
 * rows where a disruptor should be placed
 */
const disruptorRows = computed(() =>
  props.disruptors.map(
    (disruptor) => parseInt(disruptor.insert_in_row!, 10) ?? 0,
  ),
)

/**
 * position of disruptor used for css order
 */
const disruptorOrders = computed(() =>
  disruptorRows.value.map((row) => row * columns.value + 1),
)

/**
 * create a mixed array of disruptors & product indices with disruptors having the same
 * index as the product so we can use this array to see which product indices are in which row
 */
const itemOrder = computed(() =>
  [
    // Array of product indices
    ...Array.from({ length: props.products.length }).map((_v, k) => k),
    // Array of disruptor orders (-1)
    ...disruptorOrders.value.map((i, index) => i - 1 - index),
  ].sort((a, b) => a - b),
)

const placement = computed(() =>
  route.path.includes('search') ? 'SERP' : 'PLP',
)

const _getRowByIndex = (index: number) => {
  // find the real index considering that there are disruptors in between
  const realIndex = itemOrder.value.findIndex((v) => v === index)
  return getRowByIndex(realIndex, {
    columns: columns.value,
    page: currentPage.value,
    perPage: 24,
  })
}

const trackingCollector = ref<
  { row: number; items: Product[]; itemsPerRow: number }[]
>([])

const collectRowIntersection = (index: number) => {
  const row = _getRowByIndex(index)
  const startIndex = row * columns.value
  const rowIndices = [
    ...new Set(itemOrder.value.slice(startIndex, startIndex + columns.value)),
  ]

  if (
    trackingCollector.value.findIndex(
      (item) => item.row === row && item.itemsPerRow === columns.value,
    ) === -1
  ) {
    const itemsInRow = rowIndices
      .map((i) => toRaw(props.products[i]))
      .filter(Boolean)

    trackingCollector.value.push({
      row,
      items: itemsInRow,
      itemsPerRow: columns.value,
    })

    if (itemsInRow.length) {
      emit('intersect:row', {
        row,
        items: itemsInRow,
        itemsPerRow: columns.value,
      })
    }
  }
}
</script>
